import React, { useEffect, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { rentItems } from './ColumnsContent';
import ReactTooltip from 'react-tooltip';
import { Icon } from 'components/Icon';
import { ButtonIcon } from 'components/Button/ButtonIcon';
import { useHistory } from 'react-router-dom';
import { rentTableListhasNextSelector, rentTableListSelector } from 'store/transferAct/selectors';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'hooks/useDebounce';
import { usePageVisibility } from 'hooks/useVisibility';
import { Rent } from 'store/transferAct/actions';
import { Stock } from 'store/stock/actions';
import { useFirstRender } from 'hooks/useFirstRender';
import { useQueryParam, NumberParam, withDefault } from 'use-query-params';
import { Table } from './TableContentHelper/Table';
import { SubRowAsync } from './TableContentHelper/SubRowAsync';
import { Loader } from 'components/Loader';

const reserve = 'Резерв';
const receive = 'Возврат';
const defaultLimit = 50;
const defaultOffset = 0;

const TableContent = ({ stock, search, status, item, itemQueryParam }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const debounceSearch = useDebounce(search, 500, true);
  const isVisible = usePageVisibility();
  const data = useSelector(rentTableListSelector);
  const [load, setLoad] = useState();
  const hasNext = useSelector(rentTableListhasNextSelector);
  const [limit, setLimit] = useQueryParam('limit', withDefault(NumberParam, defaultLimit));
  const [offset, setOffset] = useQueryParam('offset', withDefault(NumberParam, defaultOffset));
  const firstRender = useFirstRender();

  useEffect(() => {
    if (firstRender || (itemQueryParam && !item)) {
      return;
    }
    setLimit(defaultLimit);
    setOffset(defaultOffset);
    const query = getQuery();
    query.limit = defaultLimit;
    query.offset = defaultOffset;
    dispatch(Rent.getListAsTable(query));
  }, [status, item, debounceSearch, stock, setLoad, load]);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const getQuery = () => {
    const { string } = debounceSearch;

    let query = {
      stockID: stock?.stockID1C,
      limit,
      offset,
    };
    if (string?.length > 0) {
      query.individual = true;
      query.search = string;
    }
    if (!!status) {
      query.status = status;
    }
    if (!!item) {
      query.itemID = item.itemID;
    }
    return query;
  };

  const addTransferActs = async () => {
    if (hasNext) {
      setOffset((prevState) => {
        let offset = prevState + defaultLimit;
        const query = getQuery();
        if (limit - prevState > defaultLimit) {
          offset = limit;
          setLimit(defaultLimit);
          query.limit = defaultLimit;
        }
        query.offset = offset;
        dispatch(Rent.addToListAsTable(query));
        return offset;
      });
    }
  };

  useEffect(() => {
    if ((itemQueryParam && !item) || !stock) {
      return;
    }

    if (isVisible) {
      setLimit(defaultLimit);
      setOffset(defaultOffset);
      const query = getQuery();
      query.limit = defaultLimit;
      query.offset = defaultOffset;
      dispatch(Rent.getListAsTable(query));
      // dispatch(Stock.pingStockList());
    }
  }, [isVisible]);

  const rentStatusClassColor = useCallback(
    (act) => {
      const elem = rentItems.find((elem) => elem.status === act);
      return elem ? elem.classColor : '';
    },
    [data]
  );

  const handleRedirect = ({ type, actID1C }) => {
    if (type === 'Аренда' || type === 'Возврат') {
      history.push(`/rent/${actID1C}`);
    }
    if (type === reserve) {
      history.push(`/reservation/${actID1C}`);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: () => null,
        id: 'expander',
        Cell: ({ row }) =>
          row.original.type !== undefined &&
          row.original.hasDocuments &&
          row.original.type !== receive &&
          row.original.type !== reserve && (
            <span
              {...row.getToggleRowExpandedProps()}
              data-for="expanded-button"
              data-tip={row.isExpanded ? 'Скрыть содержимое акта' : 'Просмотреть содержимое акта'}
            >
              <ButtonIcon icon={row.isExpanded ? 'vector-open' : 'vector-close'} />
            </span>
          ),
        SubCell: () => null,
      },
      {
        Header: 'Контрагент',
        accessor: (d) => d.clientName,
        Cell: (d) => (
          <span
            onClick={() => {
              handleRedirect(d.row.original);
            }}
          >
            {d.value}
          </span>
        ),
      },
      {
        Header: 'Тип',
        accessor: (d) => d.type,
        Cell: (d) => (
          <div className="table-flex flex-center">
            <span>{d.value}</span>
            {d.row.original.conduct && <Icon name="approved-doc" />}
          </div>
        ),
      },
      {
        Header: 'Статус',
        accessor: (d) => d.status,
        minWidth: 270,
        Cell: (d) =>
          !d.value || d.row.original.type === receive ? (
            ''
          ) : (
            <span className={`${rentStatusClassColor(d.value)} btn-label`}>{d.value}</span>
          ),
      },
      {
        Header: 'Склад / Касса',
        accessor: (d) => d.stockName,
      },
      {
        Header: 'Сумма',
        accessor: (d) => d.type !== reserve && d.sum !== undefined && `${d.sum} грн.`,
      },
      {
        Header: 'Залог',
        accessor: (d) => d.type !== reserve && d.pledge !== undefined && `${d.pledge} грн.`,
      },
      {
        Header: 'Дата окончания',
        accessor: (d) => d.endDateTime && moment(d.endDateTime * 1000).format('hh:mm DD.MM.YYYY'),
      },
      {
        Header: 'Инструмент',
        accessor: (d) => (d.chosenItem ? d?.chosenItem.map((data) => data.title || '').join(' , ') : ''),
        Cell: (d) => (
          <span
            className="table-content-th"
            data-for="instruments-table"
            data-tip={d.value.length <= 18 ? '' : d.value}
          >
            {d.value.length <= 18 ? d.value : `${d.value.substring(0, 16)}...`}
          </span>
        ),
      },
      {
        Header: 'Комментарий',
        accessor: (d) => d.comment,
        Cell: (d) => (
          <span className="table-content-th" data-for="comments-table" data-tip={d.value.length <= 18 ? '' : d.value}>
            {d.value.length <= 18 ? d.value : `${d.value.substring(0, 16)}...`}
          </span>
        ),
      },
    ],
    []
  );

  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => (
      <SubRowAsync row={row} rowProps={rowProps} visibleColumns={visibleColumns} onClick={handleRedirect} />
    ),
    []
  );

  return (
    <div className="table-content-container dropdownScroll">
      {data !== null ? (
        <Table
          columns={columns}
          data={data}
          onScroll={addTransferActs}
          renderRowSubComponent={renderRowSubComponent}
          onClick={handleRedirect}
        />
      ) : (
        <div className="load-block">
          <Loader color="#ddd" />
        </div>
      )}
      <ReactTooltip id="expanded-button" place="bottom" effect="solid" />
      <ReactTooltip id="comments-table" place="bottom" effect="solid" />
      <ReactTooltip id="instruments-table" place="bottom" effect="solid" />
    </div>
  );
};

TableContent.propTypes = {
  stock: PropTypes.any,
  status: PropTypes.string,
  item: PropTypes.object,
  itemQueryParam: PropTypes.string,
  search: PropTypes.shape({
    item: PropTypes.string,
    string: PropTypes.string,
  }),
};

TableContent.defaultProps = {
  stock: null,
  search: {
    string: '',
    item: null,
  },
};

export { TableContent };
